import {AddressElement, PaymentElement, useElements, useStripe} from "@stripe/react-stripe-js"
import {useMutation} from "@tanstack/react-query"
import {Spin, Radio, Space, DatePicker} from "antd"
import moment from "moment"
import {BaseButton, BasePopup, KlassDropdown} from "components"
import {handleError, toastSuccess} from "helpers"
import {preventMinusCharacter} from "helpers/inputHelper"
import {useStudentPaymentMethods} from "hooks"
import {useCallback, useEffect, useMemo, useState} from "react"
import {useTranslation} from "react-i18next"
import NumberFormat from "react-number-format"
import {studentPaymentService} from "services"
import styles from "./RecurringPayments.module.css"
import {FormLabel} from "components/Form"
import {PaymentProvider, RecurringPaymentInterval, StudentRecurringPayment} from "types/student-account/student-payment"
import StripeElementsProvider from "providers/StripeElementsProvider"

type Props = {
    isShow: boolean
    onClose: () => void
    onSuccess: () => void
    profileId: number
}

const PAYMENT_METHOD_NEW = -1

const RecurringPaymentPopup = ({isShow, onClose, onSuccess, profileId}: Props) => {
    const {t} = useTranslation(["payment", "common"])
    const [loadKey, setLoadKey] = useState(Math.random())

    useEffect(() => {
        if (!isShow) {
            setLoadKey(Math.random())
        }
    }, [isShow])

    return (
        <BasePopup isShow={isShow} onClose={onClose} leftIcon="CREDIT_CARD" width="60vw">
            <StripeElementsProvider profileId={profileId} loadKey={loadKey}>
                <h1 className={styles.title}>{t("title.addRecurringPayment")}</h1>
                <PaymentMethodForm profileId={profileId} onSuccess={onSuccess} />
            </StripeElementsProvider>
        </BasePopup>
    )
}

export default RecurringPaymentPopup

const PaymentMethodForm = ({profileId, onSuccess}: {profileId: number; onSuccess: () => void}) => {
    const {t} = useTranslation(["payment", "common"])
    const stripe = useStripe()
    const elements = useElements()
    const {paymentMethods, refetch: refetchPaymentMethods} = useStudentPaymentMethods({profileId})
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<number | undefined>()
    const [amount, setAmount] = useState<number>(0)
    const [interval, setInterval] = useState<RecurringPaymentInterval>(RecurringPaymentInterval.Month)
    const [cancelAt, setCancelAt] = useState<moment.Moment | null>(null)

    const intervalOptions = useMemo(
        () => Object.values(RecurringPaymentInterval).map((interval) => ({id: interval, name: interval})),
        []
    )

    const {isLoading, mutate: addRecurringPayment} = useMutation({
        mutationFn: async (event: React.FormEvent) => {
            event.preventDefault()

            if (!stripe || !elements || !profileId) {
                return
            }

            let paymentMethodId = selectedPaymentMethodId

            if (selectedPaymentMethodId === PAYMENT_METHOD_NEW) {
                const {error, setupIntent} = await stripe.confirmSetup({
                    elements,
                    redirect: "if_required"
                })
                if (error) {
                    throw error
                }
                const paymentMethod = await studentPaymentService.stripeAttachPaymentMethod(
                    profileId,
                    setupIntent.payment_method as string
                )
                paymentMethodId = paymentMethod.paymentMethodId
            }

            if (!paymentMethodId) {
                throw new Error(t("msg.recurringPayment.paymentMethodRequired"))
            }

            return studentPaymentService.createStudentRecurringPayment({
                profileId,
                provider: PaymentProvider.Stripe,
                paymentMethodId,
                amountInCents: Math.round((amount || 0) * 100),
                currency: "usd",
                interval,
                cancelAt: cancelAt?.toISOString() ?? null
            })
        },
        onError: (error) => handleError(error),
        onSuccess: (recurringPayment: StudentRecurringPayment) => {
            toastSuccess(t("msg.recurringPayment.addedSuccessfully"))
            onSuccess()
            refetchPaymentMethods()
            setSelectedPaymentMethodId(recurringPayment.paymentMethodId)
        }
    })

    return (
        <form onSubmit={addRecurringPayment}>
            <div className="">
                <FormLabel label={t("label.amount")} />
                <NumberFormat
                    thousandSeparator
                    className={styles.input}
                    value={amount}
                    min={0}
                    decimalScale={2}
                    onValueChange={({floatValue}) => setAmount(floatValue)}
                    onKeyDown={preventMinusCharacter}
                />
            </div>

            <div className="mt-24">
                <FormLabel label={t("label.interval")} />
                <KlassDropdown
                    isClearable={false}
                    options={intervalOptions}
                    value={intervalOptions.find((option) => option.id === interval)}
                    onChange={(option) => setInterval(option.id as RecurringPaymentInterval)}
                />
            </div>

            <div className="mt-24">
                <FormLabel label={t("label.cancelAt", "Cancel At (optional)")} />
                <DatePicker
                    style={{width: "100%"}}
                    value={cancelAt}
                    onChange={setCancelAt}
                    disabledDate={(date) => date.isBefore(moment(), "day")}
                    allowClear
                />
            </div>

            <div className="mt-24">
                <FormLabel label={t("label.paymentMethods")} />
                <Radio.Group
                    className="mt-24"
                    onChange={(e) => setSelectedPaymentMethodId(e.target.value)}
                    value={selectedPaymentMethodId}>
                    <Space direction="vertical">
                        {paymentMethods.map((method) => (
                            <Radio key={method.paymentMethodId} value={method.paymentMethodId}>
                                {`${method.cardBrand.toUpperCase()} **** **** **** ${method.last4} (${
                                    method.expMonth
                                }/${method.expYear})`}
                            </Radio>
                        ))}
                        <Radio value={PAYMENT_METHOD_NEW}>{t("label.addNewCard")}</Radio>
                    </Space>
                </Radio.Group>

                {selectedPaymentMethodId === PAYMENT_METHOD_NEW && (
                    <>
                        <AddressElement options={{mode: "billing"}} />
                        <PaymentElement className="mt-24" />
                    </>
                )}
            </div>

            <BaseButton
                size="small"
                title={t("common:action.add")}
                disabled={!stripe || !elements}
                htmlType="submit"
                loading={isLoading}
                className="w-full mt-24"
            />
        </form>
    )
}
